/**
 * The capability ladder.
 *
 * The four capability approvals are not four independent facts — they are rungs,
 * and the schema already says so: `order` runs manual (30) → shadow-mode (40) →
 * autonomous testing (50) → autonomous production (60), least to most. Four
 * columns spent a quarter of the table's width making the reader re-derive one
 * number: how far up this tractor has got.
 *
 * So the table shows the ladder as one column. The three things that collapse
 * badly are handled rather than dropped:
 *
 * - **The rungs aren't booleans.** `YES_CARGO_ONLY` and `YES_BAGGAGE_ONLY` scope
 *   an approval to a payload; `WITH_SPECIAL_PERMISSION` and `POSSIBLY_YES` are
 *   conditional. "Approved for autonomous testing" on its own is not the fact
 *   anyone dispatching needs, so the qualifier travels with the rung.
 * - **`N/A` and `not possible` are off the ladder, not failures.** A tractor
 *   whose autonomous production approval is `NOT_APPLICABLE` has not fallen
 *   short of the top rung; it is not on that rung's ladder at all. Scoring it as
 *   a failure would read as a downgrade that never happened.
 * - **A ragged ladder is a finding.** Nothing guarantees the rungs fill from the
 *   bottom. A tractor cleared for autonomous production but refused for manual
 *   ops is either a data error or something worth knowing, and a bare "highest
 *   rung" label would hide it behind a reassuring answer. `ragged` carries it up
 *   to the cell.
 *
 * Everything here is derived from the schema — group, type and `order` — so a
 * fifth capability approval added in `tractor_config.py` becomes a rung in the
 * right place with no change on this side.
 */
import {
  AttrValue,
  ConfigSchema,
  SchemaField,
  SchemaOption,
} from "./configTypes";
import { optionFor } from "./configModel";

/**
 * What one rung says about this tractor.
 *
 * Read off `severity` and `glyph` rather than the option's value, because the
 * vocabulary is the backend's to change: `severity: "ok"` is the schema saying
 * "this is the good outcome" whatever it happens to be called, and the `dash`
 * glyph is how it marks a value that isn't a judgement at all.
 */
export type RungState =
  /** Approved outright. */
  | "held"
  /** Approved with a condition, or only probably approved. */
  | "conditional"
  /** Explicitly refused. */
  | "denied"
  /** Not applicable / not possible — this rung doesn't apply to this tractor. */
  | "off"
  /** Nothing stored, or a value the schema no longer lists. */
  | "unset";

export interface CapabilityRung {
  field: SchemaField;
  value: AttrValue;
  option: SchemaOption | undefined;
  state: RungState;
}

export interface CapabilityLadder {
  /** Every capability rung, ascending. Empty when the schema has none. */
  rungs: CapabilityRung[];
  /** The highest rung held outright, or null if none is. */
  top: CapabilityRung | null;
  /**
   * Rungs below `top` that are outright refused — the ladder has a hole under
   * the tractor's own high-water mark. Empty in the ordinary case.
   *
   * Refusals only, deliberately. A *conditional* rung under a higher approval
   * ("possibly in shadow mode", but cleared for autonomous production) is
   * untidy rather than contradictory, and common enough that flagging it would
   * put a warning on half the fleet — at which point the marker stops meaning
   * anything. The conditional rung is still visible in the pips and spelled out
   * in the tooltip.
   */
  ragged: CapabilityRung[];
  /** No capability field carries a value at all: nothing has been recorded. */
  unknown: boolean;
}

/**
 * The capability rungs in schema order.
 *
 * Enums only. A rung is a state with a severity and a glyph; the approved-route
 * list is neither, and belongs with the detail rather than in the ladder.
 */
export const capabilityFields = (schema: ConfigSchema | null): SchemaField[] =>
  (schema?.fields ?? [])
    .filter((f) => f.group === "capability" && f.type === "enum")
    .sort((a, b) => a.order - b.order);

function rungState(option: SchemaOption | undefined): RungState {
  if (!option) return "unset";
  // Checked before severity: `NOT_POSSIBLE` is marked `bad` and `NOT_APPLICABLE`
  // is marked `neutral`, but both are dashes — the schema's way of saying the
  // question doesn't apply here. Reading severity first would file one of them
  // as a refusal.
  if (option.glyph === "dash") return "off";
  if (option.severity === "ok") return "held";
  if (option.severity === "warn") return "conditional";
  if (option.severity === "bad") return "denied";
  return "unset";
}

/** Where one tractor has got to, and whether the ground under it is solid. */
export function capabilityLadder(
  schema: ConfigSchema | null,
  attrs: Record<string, AttrValue>,
): CapabilityLadder {
  const rungs: CapabilityRung[] = capabilityFields(schema).map((field) => {
    const value = attrs[field.name] ?? null;
    const option = optionFor(field, value);
    return { field, value, option, state: rungState(option) };
  });

  let topIndex = -1;
  rungs.forEach((rung, i) => {
    if (rung.state === "held") topIndex = i;
  });

  return {
    rungs,
    top: topIndex >= 0 ? rungs[topIndex] : null,
    ragged: rungs
      .slice(0, Math.max(topIndex, 0))
      .filter((r) => r.state === "denied"),
    unknown: rungs.length === 0 || rungs.every((r) => r.state === "unset"),
  };
}

/**
 * How high the tractor has climbed, as a number the table can sort on.
 *
 * 1-based rung position, 0 for a tractor holding none. Sorting on this puts the
 * fleet in ladder order, which is the comparison the column exists to make —
 * sorting on the rendered label would order them alphabetically, and "Auto
 * production" would sort above "Manual" by accident rather than by meaning.
 */
export const ladderHeight = (ladder: CapabilityLadder): number =>
  ladder.top ? ladder.rungs.indexOf(ladder.top) + 1 : 0;

/**
 * Short names for the cell, which has room for a rung and not a sentence.
 *
 * Presentation only, and deliberately partial: a rung with no entry here renders
 * under its full schema label, so a new capability approval is merely verbose
 * rather than broken.
 */
const SHORT_LABEL: Record<string, string> = {
  manual_ops_approval: "Manual",
  shadow_mode: "Shadow",
  autonomy_testing_approval: "Auto testing",
  autonomy_production_approval: "Auto production",
};

export const rungLabel = (field: SchemaField): string =>
  SHORT_LABEL[field.name] ?? field.label;

/** One line per rung — the cell's tooltip, and the honest version of it. */
export function ladderSummary(ladder: CapabilityLadder): string {
  if (ladder.unknown) return "No capability recorded";
  const lines = ladder.rungs.map((rung) => {
    const value = rung.option?.label ?? "—";
    return `${rungLabel(rung.field)}: ${value}`;
  });
  if (ladder.ragged.length > 0) {
    lines.push(
      `⚠ Cleared higher than ${ladder.ragged
        .map((r) => rungLabel(r.field).toLowerCase())
        .join(" and ")}`,
    );
  }
  return lines.join("\n");
}
